/* eslint-disable react/prop-types */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable import/no-unresolved */
/* eslint-disable react/react-in-jsx-scope */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import Timeline, { DateHeader, SidebarHeader, TimelineHeaders } from "react-calendar-timeline";

import { ConfigProvider, Select, Skeleton, Alert, Modal, Spin, Button, Segmented, Tooltip } from "antd";
import { DownOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";

import { useHistory } from "react-router-dom";

import "react-calendar-timeline/style.css";
import "./ReservationTimeline.css";

import { parsePhoneNumberFromString } from "libphonenumber-js";
import apiClient from "../../../helpers/apiClient";
import cn from "../../../utilities/tailwind-merge";
import { useToast } from "../../../contexts/ToastContext";

import formatReservations, { ROW_HEIGHT } from "./formatReservations";

dayjs.extend(isoWeek);

const ONE_HOUR = 1000 * 60 * 60;
const DEFAULT_SIDEBAR_WIDTH = 200;

/* Height of the weekday / hour strip, in px. */
const HEADER_HEIGHT = 60;
/* Below this on-screen bar width the item label stacks onto two lines. */
const STACK_LABEL_BELOW_PX = 168;

const STATUS_STYLES = {
    completed: "!bg-[#0FA47A] !text-white",
    cancelled: "!bg-[#E5484D] !text-white",
    rejected: "!bg-[#E5484D] !text-white",
    ongoing: "!bg-[#5D5DF6] !text-white",
    upcoming: "!bg-[#EAEAFC] !text-[#5D5DF6]",
    returnToday: "!bg-[#F5A623] !text-white",
};

const LEGEND = [
    { key: "upcoming", color: "#DEDEFB" },
    { key: "ongoing", color: "#5D5DF6" },
    { key: "completed", color: "#0FA47A" },
];

const SEGMENTED_THEME = {
    components: {
        Segmented: {
            trackBg: "#F1F1F6",
            trackPadding: 3,
            itemColor: "#6B7280",
            itemHoverColor: "#15151F",
            itemSelectedBg: "#FFFFFF",
            itemSelectedColor: "#15151F",
            borderRadius: 9,
            borderRadiusSM: 6,
            controlHeight: 32,
            fontSize: 13,
        },
    },
};

const CAR_SELECT_THEME = {
    components: {
        Select: {
            colorBorder: "transparent",
            colorBgContainer: "transparent",
            optionSelectedBg: "#F1F1FD",
            fontSize: 14,
        },
    },
};

const DetailRow = ({ label, value }) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-xs text-gray-500 font-medium">{label}</span>
        <span className="text-sm">{value != null && String(value).trim() ? String(value).trim() : "-"}</span>
    </div>
);

const ReservationModalContent = ({ reservation, formatReservationDate, formatReservationTime, formatPhone, tRes }) => (
    <div className="rounded-lg border border-gray-200 p-4 bg-gray-50 space-y-3">
        <DetailRow label={tRes("reservationID")} value={reservation.id} />
        <DetailRow
            label={`${tRes("pickupDate")} / ${tRes("pickupTime")}`}
            value={`${formatReservationDate(reservation.pickupDate)} ${formatReservationTime(reservation.pickupDate)}`}
        />
        <DetailRow
            label={tRes("dropoffTime")}
            value={`${formatReservationDate(reservation.dropoffDate)} ${formatReservationTime(reservation.dropoffDate)}`}
        />
        <DetailRow
            label={tRes("carInfo")}
            value={[
                reservation?.carSelected?.name || reservation?.carType || tRes("notAvailable"),
                reservation?.carSelected?.carNumber && `${tRes("plateNumber")}: ${reservation.carSelected.carNumber}`,
                reservation?.carSelected?.baseLocation &&
                    `${tRes("location")}: ${reservation.carSelected.baseLocation}`,
            ]
                .filter(Boolean)
                .join(" | ")}
        />
        <DetailRow
            label={tRes("customerInfo")}
            value={[
                reservation?.customerName || tRes("notAvailable"),
                reservation?.mobileNumber && formatPhone(reservation.mobileNumber),
                reservation?.email,
            ]
                .filter(Boolean)
                .join(" | ")}
        />
    </div>
);

/* "12A", "1A", "12P", "11P" - the compact hour ticks used by the day view. */
const formatHourTick = (date) => `${date.format("h")}${date.format("A").charAt(0)}`;

const NavButton = ({ onClick, label, children }) => (
    <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="grid h-7 w-7 place-items-center rounded-md text-[#8A8A9E] transition-colors hover:bg-[#F4F4F8] hover:text-[#15151F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#5D5DF6]">
        {children}
    </button>
);

const ReservationTimelineContainer = () => {
    const history = useHistory();
    const { t } = useTranslation("dashboard");
    const { t: tRes } = useTranslation("reservations");
    const toast = useToast();

    const [reservations, setReservations] = useState([]);
    const [carTypes, setCarTypes] = useState([]);
    const [selectedCarType, setSelectedCarType] = useState("");

    const [viewMode, setViewMode] = useState("week"); // "week" | "day"
    const [anchorDate, setAnchorDate] = useState(dayjs().startOf("day"));

    const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
    const [wrapperWidth, setWrapperWidth] = useState(0);
    const timelineWrapperRef = useRef(null);

    useEffect(() => {
        const updateSidebarWidth = () => {
            setSidebarWidth(window.innerWidth < 1024 ? 112 : DEFAULT_SIDEBAR_WIDTH);
        };

        updateSidebarWidth();
        window.addEventListener("resize", updateSidebarWidth);

        return () => {
            window.removeEventListener("resize", updateSidebarWidth);
        };
    }, []);

    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isActionModalOpen, setIsActionModalOpen] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [modalMode, setModalMode] = useState(null); // 'start' | 'end'

    const days = viewMode === "week" ? 7 : 1;
    // Monday-first weeks, matching the MON..SUN header in the design.
    const rangeStart = viewMode === "week" ? anchorDate.startOf("isoWeek") : anchorDate.startOf("day");
    const rangeEnd = rangeStart.add(days, "day");

    const visibleTimeStart = rangeStart.valueOf();
    const visibleTimeEnd = rangeEnd.valueOf();

    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;

        const fetchCarTypes = async () => {
            try {
                const res = await apiClient.get("/api/car-category-v2/list?limit=1000", { signal });
                if (res?.data?.success) {
                    setCarTypes(res?.data?.data || []);
                }
            } catch (_error) {
                if (_error.name !== "AbortError") {
                    setError(_error?.response?.data || _error);
                }
            }
        };

        fetchCarTypes();

        return () => {
            controller.abort();
        };
    }, []);

    const fetchReservations = useCallback(async () => {
        try {
            const startDateTime = rangeStart.startOf("day").format("YYYY-MM-DD HH:mm:ss");
            const endDateTime = rangeEnd.endOf("day").format("YYYY-MM-DD HH:mm:ss");

            setLoading(true);
            const res = await apiClient.get(
                `/api/reservation/timeline?${
                    selectedCarType ? `carType=${selectedCarType}&` : ""
                }startDate=${startDateTime}&endDate=${endDateTime}`,
            );

            if (res?.data?.success) {
                const formattedData = formatReservations(res?.data?.data);
                setGroups(formattedData?.groups || []);
                setReservations(formattedData?.items || []);
            }
        } catch (_error) {
            if (_error.name !== "AbortError") {
                setError(_error?.response?.data || _error);
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCarType, rangeStart.valueOf(), rangeEnd.valueOf()]);

    useEffect(() => {
        fetchReservations();
    }, [fetchReservations]);

    // Track the board width so item labels know how much room they actually get.
    useEffect(() => {
        const el = timelineWrapperRef.current;
        if (!el) return undefined;

        setWrapperWidth(el.getBoundingClientRect().width);
        const observer = new ResizeObserver(([entry]) => setWrapperWidth(entry.contentRect.width));
        observer.observe(el);
        return () => observer.disconnect();
    }, [loading]);

    const pxPerMs =
        wrapperWidth > sidebarWidth ? (wrapperWidth - sidebarWidth) / (visibleTimeEnd - visibleTimeStart) : 0;

    const handleModalRedirect = (reservation) => {
        if (!reservation?.id) return;

        if (modalMode === "start") {
            history.push(`/reservations/${reservation.id}/check-in`);
            return;
        }

        history.push(`/reservations/${reservation.id}/check-out`);
    };

    const handleItemClick = (item) => {
        const status = (item.status || item.title || "").toLowerCase();

        if (["completed", "cancelled", "rejected"].includes(status)) {
            history.push(`/reservations/${item.id}`);
            return;
        }

        if (["upcoming", "ongoing"].includes(status)) {
            setIsActionModalOpen(true);
            setModalLoading(true);
            setSelectedReservation(null);
            setModalMode(status === "upcoming" ? "start" : "end");

            apiClient
                .get(`/api/reservation/${item.id}`)
                .then(({ data }) => {
                    setModalLoading(false);
                    if (!data?.success || !data?.data) {
                        toast.error({
                            message:
                                status === "upcoming"
                                    ? tRes("errorStartingReservation")
                                    : tRes("_ongoing.errorEndingReservation"),
                        });
                        setIsActionModalOpen(false);
                        return;
                    }
                    const res = data.data;
                    if (status === "upcoming" && !res?.carSelected && !res?.carId) {
                        toast.error({ message: tRes("carNotAssigned") });
                        setIsActionModalOpen(false);
                        return;
                    }
                    setSelectedReservation(res);
                })
                .catch(() => {
                    setModalLoading(false);
                    setIsActionModalOpen(false);
                    toast.error({
                        message:
                            status === "upcoming"
                                ? tRes("errorStartingReservation")
                                : tRes("_ongoing.errorEndingReservation"),
                    });
                });
        }
    };

    const closeActionModal = () => {
        setIsActionModalOpen(false);
        setSelectedReservation(null);
        setModalMode(null);
    };

    const handleModalOk = () => {
        if (!selectedReservation) return;
        handleModalRedirect(selectedReservation);
        closeActionModal();
    };

    const goToPrevious = () => setAnchorDate((prev) => prev.subtract(days, "day"));
    const goToNext = () => setAnchorDate((prev) => prev.add(days, "day"));

    const rangeLabel = useMemo(() => {
        if (viewMode === "day") {
            return rangeStart.format("ddd, MMM D, YYYY");
        }
        return `${rangeStart.format("MMM D")} - ${rangeEnd.subtract(1, "day").format("MMM D, YYYY")}`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, visibleTimeStart, visibleTimeEnd]);

    const formatReservationDate = (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "");
    const formatReservationTime = (date) => (date ? dayjs(date).format("hh:mm A") : "");
    const formatPhone = (mobileNumber) =>
        mobileNumber ? (parsePhoneNumberFromString(`+${mobileNumber}`)?.formatNational() ?? mobileNumber) : "";

    const getStatusStyle = (status, isReturnToday) => {
        if (isReturnToday) return STATUS_STYLES.returnToday;
        return STATUS_STYLES[status?.toLowerCase()] || "";
    };

    if (error) {
        return (
            <div className="h-full mt-10">
                <Alert
                    message={t("errorFetchingReservations")}
                    description={error.message || t("unknownError")}
                    type="error"
                />
            </div>
        );
    }

    return (
        <div className="h-full overflow-hidden rounded-2xl bg-[#F2F2FA] sm:rounded-3xl">
            <div className="flex h-full min-h-[320px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_rgba(16,16,40,0.06)] ring-1 ring-[#ECECF3] sm:min-h-0">
                {/* ---- date navigation + view switch ---- */}
                <div className="flex min-h-[56px] shrink-0 flex-wrap items-center justify-between gap-2 border-b border-[#F0F0F5] px-3 py-3 sm:gap-3 sm:px-5">
                    <div className="flex items-center gap-1">
                        <NavButton onClick={goToPrevious} label={t("previousPeriod")}>
                            <LeftOutlined className="text-[11px]" />
                        </NavButton>
                        <span className="whitespace-nowrap px-1.5 text-sm font-semibold tracking-[-0.01em] text-[#15151F] sm:text-[15px]">
                            {rangeLabel}
                        </span>
                        <NavButton onClick={goToNext} label={t("nextPeriod")}>
                            <RightOutlined className="text-[11px]" />
                        </NavButton>
                    </div>

                    <ConfigProvider theme={SEGMENTED_THEME}>
                        <Segmented
                            value={viewMode}
                            onChange={(value) => setViewMode(value)}
                            className="!border !border-[#E8E8F0] [&_.ant-segmented-item-label]:!px-3 sm:[&_.ant-segmented-item-label]:!px-[18px] [&_.ant-segmented-item-selected]:!shadow-[0_1px_2px_rgba(16,16,40,0.08)] [&_.ant-segmented-item]:!font-medium"
                            options={[
                                { label: t("week"), value: "week" },
                                { label: t("day"), value: "day" },
                            ]}
                        />
                    </ConfigProvider>
                </div>

                {loading ? (
                    <div className="min-h-0 flex-1 overflow-auto p-6">
                        <div className="flex flex-col gap-3">
                            {[...Array(5)].map((_, index) => (
                                <Skeleton.Input key={index} active size="large" block />
                            ))}
                        </div>
                    </div>
                ) : (
                    <div ref={timelineWrapperRef} className="softcar-timeline relative min-h-0 flex-1 overflow-auto">
                        <Timeline
                            groups={groups}
                            items={reservations}
                            keys={{
                                groupIdKey: "id",
                                groupTitleKey: "title",
                                itemIdKey: "id",
                                itemTitleKey: "title",
                                itemGroupKey: "group",
                                itemTimeStartKey: "start_time",
                                itemTimeEndKey: "end_time",
                            }}
                            onItemSelect={(itemId) => {
                                const item = reservations.find((r) => r.id === itemId);
                                if (item) handleItemClick(item);
                            }}
                            itemRenderer={({ item, getItemProps }) => {
                                const statusClass = getStatusStyle(item.status || item.title, item.isReturnToday);

                                // Width of the slice that is actually on screen, so a bar
                                // running past either edge still gets a readable label.
                                const onScreenMs =
                                    Math.min(item.end_time, visibleTimeEnd) -
                                    Math.max(item.start_time, visibleTimeStart);
                                const onScreenPx = pxPerMs * Math.max(0, onScreenMs);
                                const stacked = pxPerMs > 0 && onScreenPx < STACK_LABEL_BELOW_PX;

                                return (
                                    <div
                                        {...getItemProps({
                                            className: cn(
                                                "!rounded-md !border-none overflow-hidden cursor-pointer transition-[filter] hover:brightness-[0.97]",
                                                statusClass,
                                            ),
                                        })}>
                                        {/* Sticks to the visible slice of the bar so the label
                                            stays readable when the reservation starts off-screen. */}
                                        <div className="sticky left-0 flex h-full max-w-full flex-col justify-center px-2">
                                            {stacked ? (
                                                <>
                                                    <span className="truncate text-[11px] font-medium leading-[1.25]">
                                                        {item.id}
                                                    </span>
                                                    <span className="truncate text-[11px] font-medium leading-[1.25]">
                                                        {item.customerName}
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="truncate text-[12px] font-medium leading-none">
                                                    {item.id} &middot; {item.customerName}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            }}
                            groupRenderer={({ group }) => (
                                <div className="flex h-full items-center pl-3 pr-2 sm:pl-6 sm:pr-3">
                                    <Tooltip title={group.title} placement="top">
                                        <span className="truncate text-xs font-medium text-[#1F2330] sm:text-[13.5px]">
                                            {group.title}
                                        </span>
                                    </Tooltip>
                                </div>
                            )}
                            minZoom={viewMode === "day" ? 2 * ONE_HOUR : ONE_HOUR * 24}
                            maxZoom={viewMode === "day" ? 24 * ONE_HOUR : 10 * ONE_HOUR * 24}
                            itemsSorted
                            stackItems
                            lineHeight={ROW_HEIGHT}
                            itemHeightRatio={0.6}
                            showCursorLine={false}
                            canMove={false}
                            canResize={false}
                            canChangeGroup={false}
                            onItemContextMenu={null}
                            onCanvasClick={null}
                            onItemDoubleClick={null}
                            visibleTimeStart={visibleTimeStart}
                            visibleTimeEnd={visibleTimeEnd}
                            onTimeChange={(start, end, updateScrollCanvas) => updateScrollCanvas(start, end)}
                            sidebarWidth={sidebarWidth}
                            selected={[]}
                            onItemDeselect={() => {}}
                            onItemMove={() => {}}
                            onItemResize={() => {}}>
                            <TimelineHeaders
                                className="sticky top-0 z-[90] border-none"
                                style={{ backgroundColor: "#FFFFFF", border: "none" }}>
                                <SidebarHeader>
                                    {({ getRootProps }) => (
                                        <div
                                            {...getRootProps()}
                                            className="bg-white"
                                            style={{ borderRight: "1px solid #EAEAF2" }}>
                                            <div className="relative flex h-full w-full items-center overflow-hidden pl-3 pr-2 sm:pl-6 sm:pr-3">
                                                <ConfigProvider theme={CAR_SELECT_THEME}>
                                                    <Select
                                                        variant="borderless"
                                                        className="sc-car-select"
                                                        style={{ width: "100%" }}
                                                        popupMatchSelectWidth={false}
                                                        showSearch
                                                        suffixIcon={
                                                            <DownOutlined className="!text-[10px] !text-[#15151F]" />
                                                        }
                                                        placeholder={t("allCarList")}
                                                        value={selectedCarType}
                                                        onChange={(value) => setSelectedCarType(value || "")}
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? "")
                                                                .toLowerCase()
                                                                .includes(input.toLowerCase())
                                                        }
                                                        options={[
                                                            { value: "", label: t("allCarList") },
                                                            ...(carTypes?.map((carType) => ({
                                                                value: carType.categoryName,
                                                                label: carType.categoryName,
                                                            })) || []),
                                                        ]}
                                                    />
                                                </ConfigProvider>
                                            </div>
                                        </div>
                                    )}
                                </SidebarHeader>

                                {viewMode === "week" ? (
                                    <DateHeader
                                        unit="day"
                                        height={HEADER_HEIGHT}
                                        intervalRenderer={({ getIntervalProps, intervalContext }) => {
                                            const date = dayjs(intervalContext.interval.startTime.valueOf());
                                            const isToday = date.isSame(dayjs(), "day");
                                            return (
                                                <div
                                                    {...getIntervalProps()}
                                                    className="flex h-full select-none flex-col items-center justify-center gap-[3px]">
                                                    <span
                                                        className={cn(
                                                            "text-[10px] font-semibold uppercase leading-none tracking-[0.08em]",
                                                            isToday ? "text-[#5D5DF6]" : "text-[#9A9AAE]",
                                                        )}>
                                                        {date.format("ddd")}
                                                    </span>
                                                    <span
                                                        className={cn(
                                                            "text-[13px] font-semibold leading-none sm:text-[15px]",
                                                            isToday ? "text-[#5D5DF6]" : "text-[#2B2B38]",
                                                        )}>
                                                        {date.format("D")}
                                                    </span>
                                                </div>
                                            );
                                        }}
                                    />
                                ) : (
                                    <DateHeader
                                        unit="hour"
                                        height={HEADER_HEIGHT}
                                        intervalRenderer={({ getIntervalProps, intervalContext }) => {
                                            const date = dayjs(intervalContext.interval.startTime.valueOf());
                                            return (
                                                <div
                                                    {...getIntervalProps()}
                                                    className="flex h-full select-none items-center justify-center text-[11px] font-medium leading-none text-[#9A9AAE]">
                                                    {formatHourTick(date)}
                                                </div>
                                            );
                                        }}
                                    />
                                )}
                            </TimelineHeaders>
                        </Timeline>

                        {groups.length === 0 && (
                            <div
                                className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center text-[13px] text-[#9A9AAE]"
                                style={{ top: HEADER_HEIGHT }}>
                                {t("noReservations")}
                            </div>
                        )}
                    </div>
                )}

                {/* ---- legend ---- */}
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 px-3 pb-4 pt-3 text-[11.5px] text-[#9A9AAE] sm:gap-5 sm:px-6">
                    {LEGEND.map(({ key, color }) => (
                        <span key={key} className="flex items-center gap-1.5">
                            <span
                                className="inline-block h-[7px] w-[7px] rounded-full"
                                style={{ backgroundColor: color }}
                            />
                            {t(key)}
                        </span>
                    ))}
                </div>

                <Modal
                    title={modalMode === "start" ? tRes("startReservation") : tRes("_ongoing.endReservation")}
                    open={isActionModalOpen}
                    onCancel={closeActionModal}
                    centered
                    width={520}
                    destroyOnClose
                    classNames={{
                        content: "max-w-[min(520px,calc(100vw-2rem))]",
                        footer: "block [&_.ant-btn+.ant-btn]:!ms-0",
                    }}
                    footer={
                        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-3">
                            <Button
                                key="cancel"
                                onClick={closeActionModal}
                                shape="round"
                                disabled={modalLoading}
                                block
                                className="!m-0 sm:!w-auto">
                                {tRes("cancel")}
                            </Button>
                            <Button
                                key="viewDetails"
                                type="primary"
                                shape="round"
                                disabled={modalLoading || !selectedReservation}
                                block
                                className="!m-0 sm:!w-auto"
                                onClick={() => {
                                    if (selectedReservation?.id) {
                                        history.push(`/reservations/${selectedReservation.id}`);
                                        closeActionModal();
                                    }
                                }}>
                                {tRes("_all.viewDetails")}
                            </Button>
                            <Button
                                key="ok"
                                type="primary"
                                shape="round"
                                disabled={modalLoading || !selectedReservation}
                                block
                                className="!m-0 sm:!w-auto"
                                onClick={handleModalOk}>
                                {modalMode === "start" ? tRes("startReservation") : tRes("_ongoing.endReservation")}
                            </Button>
                        </div>
                    }>
                    {modalLoading ? (
                        <div className="py-12 flex justify-center">
                            <Spin size="large" />
                        </div>
                    ) : selectedReservation ? (
                        <ReservationModalContent
                            reservation={selectedReservation}
                            formatReservationDate={formatReservationDate}
                            formatReservationTime={formatReservationTime}
                            formatPhone={formatPhone}
                            tRes={tRes}
                        />
                    ) : null}
                </Modal>
            </div>
        </div>
    );
};

export default ReservationTimelineContainer;
